import { useEffect, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { useLocale } from '../../i18n/context'

// Per-device, per-user flag: has the ignite animation already played today
// for this account on this device? Keyed by calendar day so a new day always
// gets one fresh chance to animate, and clearing localStorage only ever
// costs a repeat animation, never a wrong lit/unlit read (that always comes
// from server data — see Props.goalMet).
//
// Scoped by user id so a second account signing into the same device isn't
// silently treated as "already celebrated today" by the first account's
// entry. The pre-scoping key name (`cs_flame_animated_date`, no suffix) is
// deliberately never read anymore — it's orphaned in place rather than
// migrated, so an existing user gets at most one extra replay the first time
// this ships, never a permanently stuck flag.
const ANIMATED_TODAY_KEY_PREFIX = 'cs_flame_animated_date'

function animatedTodayKey(userId: string): string {
  return `${ANIMATED_TODAY_KEY_PREFIX}:${userId}`
}

function todayKey(): string {
  return new Date().toDateString()
}

// Resting silhouette — also the morph's start/end keyframe (Moderate variant, design of
// record docs/design/Daily_Goal_Ignite_v2.html, data-int="moderate").
const FLAME_PATH =
  'M12 2.5 C 13.5 7 19 10 19 15 C 19 18.87 15.87 22 12 22 C 8.13 22 5 18.87 5 15 C 5 10 10.5 7 12 2.5 Z'

const TONGUE_LEFT_PATH =
  'M12 3.5 C 12.6 5 13.6 6 13.6 7.4 C 13.6 8.7 12.9 9.6 12 9.6 C 11.1 9.6 10.4 8.7 10.4 7.4 C 10.4 6 11.4 5 12 3.5 Z'
const TONGUE_RIGHT_PATH =
  'M12 4.2 C 12.5 5.4 13.3 6.2 13.3 7.3 C 13.3 8.4 12.7 9.1 12 9.1 C 11.3 9.1 10.7 8.4 10.7 7.3 C 10.7 6.2 11.5 5.4 12 4.2 Z'

// CSS `d` (path-to-path) morphing is Chromium-scoped. Engines without it (Safari/iOS PWA)
// still get the fill fade, glow ramp, and tongues — the silhouette just doesn't morph.
const SUPPORTS_PATH_MORPH =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('d', 'path("M0 0 L1 1 Z")')

// Longest sub-animation in the composite (fill fade + silhouette morph + glow ramp,
// Moderate variant) — the igniting class comes off after this so the resting lit state
// is fully static.
const IGNITE_DURATION_MS = 900

/**
 * Daily-goal ignite — one composite hero (DESIGN_SYSTEM.md "Motion & Celebration" §Daily
 * goal flame). `goalMet` must be server-derived (today_resolutions >= daily_card_goal,
 * ADR-054) — never inferred from the animation flag, so a second device always renders
 * the correct state even if it never animated.
 *
 * Flame (fill fade + silhouette morph + glow ramp + two rising tongues, the Moderate
 * variant) and the streak number/unit text are gated by the same single
 * per-day-per-device-per-user flag and animate together, never independently. Animates
 * once per calendar day per device per user, the first time
 * this device observes goalMet=true for today for the signed-in account (covers both
 * the unmet→met transition and a fresh device's first sight of an already-met day).
 * Every later render that day, for that account, is fully static. The flag is keyed by
 * user id (never display name/email) so a second account on the same device is never
 * mistaken for "already celebrated today" by the first account's entry.
 *
 * The flag is only ever committed once the state has actually been presented to a
 * visible viewport (DESIGN_SYSTEM.md "Daily-goal flame" — visibility rule): a mount
 * while `document.hidden` defers presentation until the tab/TWA is foregrounded, and
 * the ignite's completion is tracked in visible-time (paused while hidden, resumed on
 * return) rather than by a wall-clock timeout, since CSS keyframes don't advance while
 * hidden but a bare `setTimeout` keeps firing.
 */
export default function DailyGoalFlame({
  goalMet,
  streakDays,
  userId,
  size = 16,
}: {
  goalMet: boolean
  streakDays: number
  userId: string
  size?: number
}) {
  const { tr } = useLocale()
  const reduceMotion = !!useReducedMotion()
  const [hasAnimatedToday, setHasAnimatedToday] = useState(
    () => localStorage.getItem(animatedTodayKey(userId)) === todayKey(),
  )
  const [igniting, setIgniting] = useState(false)

  // Re-derives from this account's own key if userId changes under a persisted
  // component instance (e.g. an in-app account switch without a full remount).
  useEffect(() => {
    setHasAnimatedToday(localStorage.getItem(animatedTodayKey(userId)) === todayKey())
  }, [userId])

  const commitAnimatedToday = () => {
    localStorage.setItem(animatedTodayKey(userId), todayKey())
    setHasAnimatedToday(true)
  }

  // Presents the ignite (or, under reduced motion, the immediate final state) the
  // first time this device is both goal-met-and-unrecorded AND actually visible —
  // deferring across a visibilitychange if the mount happens while hidden.
  useEffect(() => {
    if (!goalMet || hasAnimatedToday) return

    const present = () => {
      if (reduceMotion) {
        // Nothing to animate, but the static final state is only "presented" once
        // a visible viewport has shown it — which is guaranteed here since present()
        // only runs on/after a visible check.
        commitAnimatedToday()
      } else {
        setIgniting(true)
      }
    }

    if (document.visibilityState === 'visible') {
      present()
      return
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        present()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [goalMet, hasAnimatedToday, reduceMotion, userId])

  // Closes the ignite window and commits the flag only once IGNITE_DURATION_MS of
  // *visible* time has elapsed — accumulation pauses while hidden and resumes on
  // return, so a backgrounded mid-animation tab finishes to the static lit state
  // instead of being cut short by an elapsed-while-hidden wall-clock timer.
  useEffect(() => {
    if (!igniting) return

    let remaining = IGNITE_DURATION_MS
    let segmentStart: number | null = null
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const finish = () => {
      setIgniting(false)
      commitAnimatedToday()
    }

    const startSegment = () => {
      segmentStart = Date.now()
      timeoutId = setTimeout(finish, remaining)
    }

    const pauseSegment = () => {
      if (segmentStart !== null) {
        remaining -= Date.now() - segmentStart
        segmentStart = null
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
        timeoutId = undefined
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') startSegment()
      else pauseSegment()
    }

    if (document.visibilityState === 'visible') startSegment()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }, [igniting])

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
      {igniting && (
        <style>{`
          @keyframes csFlameOutlineOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes csFillIn {
            0%   { opacity: 0; fill: #CDBB9A; }
            35%  { opacity: 1; fill: #D9B070; }
            100% { opacity: 1; fill: var(--celebration-lit-flame); }
          }
          @keyframes csMorphModerate {
            0%   { d: path("M12 10 C 14 12 18 12.5 18 15.5 C 18 18.87 15.5 22 12 22 C 8.5 22 6 18.87 6 15.5 C 6 12.5 10 12 12 10 Z"); }
            35%  { d: path("M13 2 C 15.5 6.5 19.5 10 19.5 15 C 19.5 18.87 15.87 22 12 22 C 8.13 22 4.5 18.87 4.5 14.5 C 4.5 10 9.5 6.5 13 2 Z"); }
            70%  { d: path("M12 1.5 C 14 6 20 10 20 15 C 20 18.87 15.87 22 12 22 C 8.13 22 4 18.87 4 15 C 4 10 10 6 12 1.5 Z"); }
            100% { d: path("${FLAME_PATH}"); }
          }
          @keyframes csGlowModerate {
            0%   { filter: none; }
            45%  { filter: drop-shadow(0 0 7px var(--celebration-lit-flame-glow)); }
            100% { filter: drop-shadow(0 0 2.5px var(--celebration-lit-flame-glow)); }
          }
          @keyframes csTongueRiseLeft {
            0%   { opacity: 0;   transform: translate(-2.5px, 2px) scale(.2); }
            30%  { opacity: .85; }
            62%  { opacity: .45; transform: translate(-2.5px, -5px) scale(1); }
            100% { opacity: 0;   transform: translate(-2.5px, -9px) scale(.7); }
          }
          @keyframes csTongueRiseRight {
            0%   { opacity: 0;   transform: translate(2.8px, 2px) scale(.2); }
            30%  { opacity: .85; }
            62%  { opacity: .45; transform: translate(2.8px, -5px) scale(1); }
            100% { opacity: 0;   transform: translate(2.8px, -9px) scale(.7); }
          }
          @keyframes csStreakSpring { from { opacity: 0; transform: scale(.5); } to { opacity: 1; transform: scale(1); } }
          @keyframes csStreakUnitFade { 0% { opacity: 0; } 45% { opacity: 0; } 100% { opacity: 1; } }
        `}</style>
      )}

      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          display: 'inline-flex',
          width: size,
          height: size,
          flexShrink: 0,
          animation: igniting ? 'csGlowModerate 900ms ease-out both' : undefined,
          // Permanent resting glow (ADR: flame subsection) — same token/radius as the
          // ignite's settled frame, so an animated-in flame and an ordinary lit render
          // (e.g. a returning device that already recorded today's animation) are
          // pixel-identical. Unlit never gets a glow.
          filter: !igniting && goalMet ? 'drop-shadow(0 0 2.5px var(--celebration-lit-flame-glow))' : undefined,
        }}
      >
        <svg viewBox="0 0 24 24" width={size} height={size} style={{ overflow: 'visible', position: 'absolute', inset: 0 }}>
          {/* Unlit layer: outline only, muted. Hidden once lit and settled. */}
          <path
            d={FLAME_PATH}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              opacity: goalMet ? 0 : 1,
              animation: igniting ? 'csFlameOutlineOut 200ms ease-out both' : undefined,
            }}
          />
          {/* Lit layer: filled, celebration-palette amber (exclusive to this element).
              Silhouette morph only runs where CSS `d` animation is supported; elsewhere
              the fill fade + glow still play against the static resting silhouette. */}
          <path
            d={FLAME_PATH}
            fill="var(--celebration-lit-flame)"
            style={{
              opacity: goalMet ? 1 : 0,
              animation: igniting
                ? SUPPORTS_PATH_MORPH
                  ? 'csFillIn 900ms ease-out both, csMorphModerate 900ms cubic-bezier(.33,.05,.2,1) both'
                  : 'csFillIn 900ms ease-out both'
                : undefined,
            }}
          />
          {/* Tongues: ignite-only, never rendered on a resting frame (lit or unlit). */}
          {igniting && (
            <>
              <path
                d={TONGUE_LEFT_PATH}
                fill="var(--celebration-lit-flame-tongue)"
                style={{
                  transformBox: 'fill-box',
                  transformOrigin: '50% 100%',
                  animation: 'csTongueRiseLeft 540ms ease-out 170ms both',
                }}
              />
              <path
                d={TONGUE_RIGHT_PATH}
                fill="var(--celebration-lit-flame-tongue)"
                style={{
                  transformBox: 'fill-box',
                  transformOrigin: '50% 100%',
                  animation: 'csTongueRiseRight 500ms ease-out 300ms both',
                }}
              />
            </>
          )}
        </svg>
      </span>

      <span style={{ fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'baseline', gap: 3 }}>
        <span
          style={{
            display: 'inline-block',
            transformOrigin: '50% 60%',
            animation: igniting ? 'csStreakSpring 780ms cubic-bezier(.34,1.56,.64,1) both' : undefined,
          }}
        >
          {streakDays}
        </span>
        <span style={{ animation: igniting ? 'csStreakUnitFade 620ms ease-out both' : undefined }}>
          {tr.home.streakUnit(streakDays)}
        </span>
      </span>
    </span>
  )
}
